import useSWRMutation from 'swr/mutation'
import useSWR, { mutate } from 'swr'

import { dismissInterview, getAudioInterviewSocketUrl, getLastInterview, getLastInterviewForJobOfferId, startInterview } from '../../../api/gpt'
import { ANALYTICS_EVENTS, useAnalytics } from '../../../libraries/posthog'
import { InterviewState } from '../../../api/gpt/types'
import { useI18N } from '../../../libraries/i18n/hook'

// Constants for cache keys
const CACHE_BASE = 'ai-interview'
const JOB_OFFER_BASE = 'job-offer'
const CACHE_KEYS = {
  INTERVIEW_JOB_OFFER: (jobOfferId: string, topic: string) => [CACHE_BASE, JOB_OFFER_BASE, jobOfferId, topic],
  AUDIO_INTERVIEW_SOCKET_URL: (sessionId: string) => [CACHE_BASE, 'socket', sessionId],
  LAST_INTERVIEW: [CACHE_BASE, JOB_OFFER_BASE, 'last'],
  START: 'start',
  DISMISS: 'dismiss',
} as const

export const useLastActiveInterview = () => {
  return useSWR(
    CACHE_KEYS.LAST_INTERVIEW,
    async () => {
      const interviewData = await getLastInterview()
      if(interviewData?.isDismissed
          || interviewData?.state === InterviewState.INITIAL
        ) {
        return null
      }
      return interviewData
    }
  )
}

export const useInterviewForJobOffer = (jobOfferId?: string, topic?: string) => {
  if(!jobOfferId || !topic) {
    return { data: null, isLoading: false, error: 'Job offer ID or topic not provided' }
  }
  return useSWR(
    CACHE_KEYS.INTERVIEW_JOB_OFFER(jobOfferId, topic),
    async () => {
      const interviewData = await getLastInterviewForJobOfferId(jobOfferId, topic)
      return interviewData
    }
  )
}

export const useDismissInterview = () => {
  return useSWRMutation(
    CACHE_KEYS.DISMISS,
    async (_: string, { arg: { sessionId }  }: { arg: { sessionId: string }}) => {
      await dismissInterview(sessionId)
    }, {
      onSuccess: () => { invalidateInterviewCache() },
    }
  )
}

type StartInterviewArgs = {
  arg: {
    topic: string
    numberOfMainQuestions: number
    numberOfFollowUpQuestions: number
    description?: string
    jobOfferId?: string
  }
}
export const useStartInterview = () => {
  const { t } = useI18N()
  const { trackEvent } = useAnalytics()
  return useSWRMutation(
    CACHE_KEYS.START,
    async (_: string, { arg: { topic, numberOfMainQuestions, numberOfFollowUpQuestions, description, jobOfferId  } }: StartInterviewArgs) => {
      try {
        const response = await startInterview(
           topic,
           numberOfMainQuestions,
           numberOfFollowUpQuestions,
           description,
           jobOfferId,
        )
        return response
      } catch (error: unknown) {
        if(error instanceof Error && error.message.includes('not a valid job description')) {
          throw new Error(t('interviewer.ai.jobDescription.error.message'))
        }
        throw error
      }
    }, {
      onSuccess: (response) => {
        invalidateInterviewCache()
        trackEvent(ANALYTICS_EVENTS.INTERVIEW_STARTED, {
          session_id: response.session_id,
          topic: response.topic,
          total_main_questions: response.total_main_questions || 0,
          max_follow_ups_per_main: response.max_follow_ups_per_main || 0,
        })
      },
    }
  )
}

export const invalidateInterviewCache = async () => {
  await mutate(key => Array.isArray(key) && key[0] === CACHE_BASE && key[1] === JOB_OFFER_BASE)
  await mutate(CACHE_KEYS.LAST_INTERVIEW)
}

export const useAudioInterviewSocketUrl = (sessionId: string) => {
  return useSWR(
    CACHE_KEYS.AUDIO_INTERVIEW_SOCKET_URL(sessionId),
    async () => {
      const data = await getAudioInterviewSocketUrl(sessionId)
      return data.wsUrl
    }
  )
}
